import axiosInstance from "../../../utils/axiosConfig";
import React, {
  useEffect,
  useState,
  forwardRef,
  useImperativeHandle,
} from "react";
import { BiDotsHorizontalRounded } from "react-icons/bi";
import { FiChevronLeft, FiChevronRight } from "react-icons/fi";
import { MdAdd } from "react-icons/md";
import { API_URL } from "../../../constant";
import { useAuth } from "../../../context/auth-context";
import toast from "react-hot-toast";
import { TailSpin } from "react-loader-spinner";
import RestrictIpModal from "./RestrictIpModal";
import { IoCheckboxOutline } from "react-icons/io5";
import EmptyData from "../../../components/EmptyData";

const IpTable = forwardRef(({ searchTerm }: any, ref) => {
  const { isAuthenticated } = useAuth();
  const [page, setPage] = useState(1);
  const [currentPage, setCurrentPage] = useState<number>();
  const [totalPages, setTotalPages] = useState<number>(1);
  const [refetch, setRefetch] = useState<number>(1);
  const [openEdit, setOpenEdit] = useState<Record<number, boolean>>({});
  const [ipLoader, setIpLoader] = useState<boolean>(false);
  const [ipLogs, setIpLogs] = useState<any[]>([]);
  const [restrictModal, setRestrictModal] = useState<boolean>(false);
  const [unrestrictLoader, setUnrestrictLoader] = useState<boolean[]>([]);

  useImperativeHandle(ref, () => ({
    getData: () => ipLogs,
  }));

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const months = [
      "Jan",
      "Feb",
      "Mar",
      "Apr",
      "May",
      "Jun",
      "Jul",
      "Aug",
      "Sep",
      "Oct",
      "Nov",
      "Dec",
    ];

    const day = date.getDate();
    const month = months[date.getMonth()];
    const year = date.getFullYear();
    const hours = date.getHours();
    const minutes = date.getMinutes();
    const ampm = hours >= 12 ? "pm" : "am";

    const formattedHours = hours % 12 || 12;
    const formattedMinutes = minutes.toString().padStart(2, "0");

    return `${month} ${day}${getDaySuffix(
      day
    )}, ${year} | ${formattedHours}:${formattedMinutes}${ampm}`;
  };

  const getDaySuffix = (day: number) => {
    if (day > 3 && day < 21) return "th";
    switch (day % 10) {
      case 1:
        return "st";
      case 2:
        return "nd";
      case 3:
        return "rd";
      default:
        return "th";
    }
  };

  const toggleModal = (objectId: number) => {
    setOpenEdit((prev) => ({
      ...prev,
      [objectId]: !prev[objectId], // Toggle the state for the specific object
    }));
  };
  const closeModal = (objectId: number) => {
    setOpenEdit((prev) => ({
      ...prev,
      [objectId]: !prev[objectId], // Close the modal for the specific object
    }));
  };

  useEffect(() => {
    if (!isAuthenticated) return; // Don't fetch before the session bootstrap has confirmed one

    setIpLoader(true);

    const fetchData = async () => {
      const searchParam = searchTerm ? `&search=${searchTerm}` : "";

      try {
        const { data } = await axiosInstance.get(
          `${API_URL}api/restricted-ips?limit=${PAGE_SIZE}&page=${page}${searchParam}`
        );

        // console.log(data.data);

        const sortedIp = data.data.restrictedIPs.sort(
          (a: any, b: any) =>
            new Date(b.restrictedAt).getTime() -
            new Date(a.restrictedAt).getTime()
        );

        setTotalPages(data.data.pagination.totalPages);
        setCurrentPage(data.data.pagination.currentPage);
        setIpLogs(sortedIp);
      } catch (error: any) {
        console.log(error.response?.data?.message || "An error occurred");
        toast.error(error.response?.data?.message || "An error occurred");
      } finally {
        setIpLoader(false);
      }
    };

    const debounceTimer = setTimeout(fetchData, 600);

    return () => clearTimeout(debounceTimer);
  }, [page, isAuthenticated, refetch, searchTerm]);

  // For now, use dummy data
  const PAGE_SIZE = 15;

  return (
    <div className="bg-[#0D0D0D] h-[calc(100vh-400px)] pb-4 w-full font-manrope flex flex-col justify-between">
      <div className="overflow-auto ">
        <table className="w-full">
          <thead className="sticky top-0 bg-[#1C2227] z-10">
            <tr className="h-[56px] flex justify-start gap-4 items-center px-4">
              <th className="text-[#617889] text-[15px] w-1/6 flex items-center gap-1 cursor-pointer">
                IP
              </th>
              <th className="text-[#617889] text-[15px] w-1/6 text-left">
                Country
              </th>
              <th className="text-[#617889] text-[15px] w-[26.667%] text-left">
                Description
              </th>

              <th className="text-[#617889] text-[15px] w-[23.6667%] text-left">
                Date Logged
              </th>
              <th className="text-[#617889] text-[15px] w-[9.6667%] text-left">
                Status
              </th>

              <th className="text-[#617889] text-[15px] w-[6.6667%] text-center">
                &nbsp;
              </th>
            </tr>
          </thead>
          {ipLoader ? (
            <tbody>
              <tr>
                <td colSpan={8}>
                  <div className="flex justify-center items-center h-40">
                    <TailSpin width={40} height={40} color="#BEE1FB" />
                  </div>
                </td>
              </tr>
            </tbody>
          ) : (
            <tbody>
              {ipLogs.length === 0 ? (
                <tr>
                  <td colSpan={8}>
                    <EmptyData />
                  </td>
                </tr>
              ) : (
                ipLogs.map((txn, idx) => {
                  const unrestrict = async () => {
                    setUnrestrictLoader((prev) => {
                      const updated = [...prev];
                      updated[idx] = true;
                      return updated;
                    });

                    try {
                      const { data: data2 } = await axiosInstance.put(
                        `${API_URL}api/restricted-ips`,
                        {
                          ip: txn?.ip,
                        }
                      );
                      setRefetch((prev: number) => prev++);
                      toast.success(`IP unrestricted successfully`);
                    } catch (error: any) {
                      console.log(
                        error.response?.data?.message || "An error occurred"
                      );
                      toast.error(
                        error.response?.data?.message || "An error occurred"
                      );
                    } finally {
                      setUnrestrictLoader((prev) => {
                        const updated = [...prev];
                        updated[idx] = false;
                        return updated;
                      });
                      closeModal(idx);
                    }
                  };
                  return (
                    <tr
                      key={idx}
                      className="h-[56px] flex justify-start items-center px-4 border-b gap-4 border-[#23282D] cursor-pointer hover:bg-[#181B1F]"
                    >
                      <td className="text-white w-1/6  text-[14px] text-left">
                        {txn.ip}
                      </td>
                      <td className="text-white w-1/6 flex flex-col items-start gap-1 whitespace-nowrap overflow-hidden text-ellipsis text-[14px] text-left">
                        <span>{txn.country} </span>
                      </td>
                      <td className="text-white w-[26.667%] text-[14px] whitespace-nowrap overflow-hidden text-ellipsis text-left">
                        {txn.reason}
                      </td>
                      <td className="text-white w-[23.6667%] text-[14px] text-left">
                        {formatDate(txn.restrictedAt)}
                      </td>

                      <td className="w-[9.6667%]">
                        <span
                          className={` text-[white] px-2 text-[11px] py-1 rounded-full text-xs font-bold 
                    bg-[#C60303]
                 
                    `}
                        >
                          Restricted
                        </span>
                      </td>
                      <td className="w-[6.6667%] flex justify-center">
                        <BiDotsHorizontalRounded
                          onClick={() => {
                            toggleModal(idx);
                          }}
                          className="text-[#869AA9] text-2xl cursor-pointer"
                        />
                        {openEdit[idx] && (
                          <div
                            onClick={() => {
                              if (openEdit[idx]) {
                                closeModal(idx);
                              }
                            }}
                            className=" fixed inset-0 flex font-manrope justify-end pr-20 bottom-32 z-50 items-center bg-black bg-opacity-20  "
                          >
                            <div className="w-[190px]   bg-[#1C2227]  ">
                              <div
                                onClick={(e) => {
                                  e.stopPropagation();
                                  unrestrict();
                                }}
                                className=" w-full  flex justify-between px-4  border-b border-[#13181B]  py-3 items-center"
                              >
                                <h4 className="text-[#359943] text-[15px]  ">
                                  Unrestrict
                                </h4>
                                <span>
                                  {unrestrictLoader[idx] ? (
                                    <TailSpin
                                      color="#ffffff"
                                      width={20}
                                      height={20}
                                    />
                                  ) : (
                                    <IoCheckboxOutline className="text-[#359943] text-[16px]" />
                                  )}
                                </span>
                              </div>
                            </div>
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })
              )}
              <tr
                onClick={() => setRestrictModal(true)}
                className="h-[56px] flex justify-start items-center px-4 border-b gap-2 border-[#23282D] cursor-pointer hover:bg-[#181B1F]"
              >
                <MdAdd className="text-secondary_button" />
                <span className="text-secondary_button font-bold">Add New</span>
              </tr>
            </tbody>
          )}
        </table>

        {/* Pagination */}
      </div>
      <div className="flex justify-end items-center px-2 py-2">
        <div className="flex gap-2 items-center">
          <span className="text-[#869AA9] text-xs">
            {currentPage} of {totalPages} page{totalPages > 1 && "s"}
          </span>
          <button
            className={`w-[27px] h-[27px] rounded-lg flex justify-center items-center text-[#1C2227] border border-[#1C2227]`}
            onClick={() => {
              if (page === 1) {
                setPage(1);
              } else {
                setPage(page - 1);
              }
            }}
            disabled={page === 1}
          >
            <FiChevronLeft className="text-[#BDBDBE] text-xl" />
          </button>
          <button
            className={`w-[27px] h-[27px] rounded-lg font-bold text-[13px] flex bg-[#BEE1FB] justify-center items-center text-[#0D0D0D] ${
              page === 1
                ? "opacity-100 ext-[#0D0D0D] bg-[#BEE1FB]"
                : " bg-transparent text-white"
            }`}
            onClick={() => setPage(1)}
            disabled={page === 1}
          >
            {page}
          </button>

          <button
            className={`w-[27px] h-[27px] rounded-lg flex justify-center items-center text-[#1C2227] border border-[#1C2227]`}
            onClick={() => {
              if (page < totalPages) {
                setPage(page + 1);
              }
            }}
            disabled={page === totalPages}
          >
            <FiChevronRight className="text-[#BDBDBE] text-xl" />
          </button>
        </div>
      </div>
      {restrictModal && (
        <RestrictIpModal
          isOpen={restrictModal}
          setRefetch={setRefetch}
          onClose={() => setRestrictModal(false)}
        />
      )}
    </div>
  );
});

export default IpTable;
